import React from "react";
import { getThemeColor, r } from "../utils";
import { withState } from "recompose";
import {
  HideDesktop,
  HideTablet,
  HideMobile,
  ShowMobile
} from "./blocks/Responsive";
import dashedImg from "../../assets/dashed.png";
import Waypoint from "react-waypoint";
import styled from "styled-components";
import ReactMarkdown from "react-markdown";
import { getFontFamily } from "../utils";
import { P, H3, Description } from "./typography";
import ProductAnimation from "../components/ProductAnimation";
import Flex from "../components/blocks/Flex";
import Spacer from "../components/blocks/Spacer";

const Grid = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DottedLine = styled.div`
  background: url(${dashedImg}) repeat-y center top;
  height: 100%;
  width: 2px;
  position: absolute;
`;

const getDootPositioning = ({ stage }) => {
  if (stage === 0)
    return `
      position: absolute;
      top: -3vh;
      bottom: auto; 
  `;
  if (stage > 3)
    return `
      position: absolute;
      bottom: -32vh;
      top: auto; 
  `;
  return `
      position: fixed;
      bottom: 20vh;
      top: 22vh; 
  `;
};

const Doots = styled.div`
  transition: top 300ms, bottom: 300ms;
  height: 50vh;
  left: 2rem;
  ${props => getDootPositioning(props)};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Doot = styled.div`
  cursor: pointer;
  z-index: 3;
  border-radius: 50%;
  height: ${({ active }) => (active ? "1.25rem" : ".625rem")};
  width: ${({ active }) => (active ? "1.25rem" : ".625rem")};
  background: ${({ active }) => (active ? "black" : "white")};
  border: .25rem solid ${getThemeColor("teal")};
  border-width: ${({ active }) => (active ? ".25rem" : ".0625rem")};
`;

const DootWrapper = styled.div`
  z-index: 3;
  padding: .5rem 0;
  background: ${({ active }) => (active ? "none" : "none")};
  transform: ${({ active, stage, index }) =>
    active
      ? "scale(2) translateY(0)"
      : "scale(1) translateY(" + (index - stage) + "rem)"};
  transition: transform 500ms;
`;

const ProductSlider = styled.div`
  position: ${({ stage }) => (stage > 0 && stage < 4 ? "fixed" : "absolute")};
  top: ${({ stage }) => (stage < 4 ? "0vh" : "auto")};
  bottom: ${({ stage }) => (stage < 4 ? "auto" : "-5vh")};
  transform: translate3d(0, ${({ stage }) => (stage < 1 ? 0 : "25vh")}, 0);
  transition: transform 0ms;
  z-index: -10;
  ${props => props.theme.media.mobile`
    transform: translate3d(0, ${({ stage }) => (stage < 1 ? 0 : "5vh")}, 0);
  `};
`;

const AnimationContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  flex-wrap: wrap-reverse;
  max-width: 1200px;
  padding: 3rem;
`;

const AnimationText = styled.div`
  padding: 3rem 2rem 3rem 1rem;
  min-width: 15rem;
  order: 1;
  flex: 3;
  ${props => props.theme.media.desktop`
padding: 1rem 2rem 3rem 1rem;
`};
`;

const AnimationSVG = styled.div`
  order: 2;
  flex: 2;
`;

const ProductTracks = styled.div`
  height: 140vh;
  position: relative;
  margin-bottom: 45vh;
`;

const StageWaypoint = ({ stage, onStageChange, position }) =>
  <Waypoint
    onPositionChange={({ currentPosition, previousPosition }) => {
      var slider = document.getElementById("productSlider");
      var doots = document.getElementById("dootsInSlider");
      if (currentPosition === "inside" && previousPosition === "above") {
        onStageChange(stage);
        if (stage == 0) {
          slider.style.position = "relative";
          slider.style.transform = "translate3d(0,0,0)";
          doots.style.position = "absolute";
          doots.style.top = "-3vh";
          doots.style.bottom = "auto";
        } else {
          slider.style.position = "";
          slider.style.transform = "";
          doots.style.position = "";
          doots.style.top = "";
          doots.style.bottom = "";
        }
      }
      if (currentPosition === "above") {
        onStageChange(stage + 1);
        slider.style.position = "";
        slider.style.transform = "";
        doots.style.position = "";
        doots.style.top = "";
        doots.style.bottom = "";
      }
    }}
  >
    <div
      className={"stage-" + stage}
      id={"stage-" + stage}
      style={{
        position: "absolute",
        top: position
      }}
    />
  </Waypoint>;

const Animation = ({
  stage,
  onStageChange,
  animationTitles = [""],
  animationDescriptions = [""]
}) => {
  const dootStage = Math.max(1, Math.min(stage, 3));

  return (
    <ProductTracks>
      <StageWaypoint stage={0} position="-25vh" onStageChange={onStageChange} />
      <StageWaypoint stage={1} position="20vh" onStageChange={onStageChange} />
      <StageWaypoint stage={2} position="60vh" onStageChange={onStageChange} />
      <StageWaypoint stage={3} position="100vh" onStageChange={onStageChange} />
      <Grid>
        <ProductSlider stage={stage} id="productSlider">
          <AnimationContainer>
            <AnimationText>
              <H3>
                {stage == 0
                  ? animationTitles[0]
                  : stage < 3 ? animationTitles[stage - 1] : animationTitles[2]}
              </H3>
              <Description>
                {stage == 0
                  ? animationDescriptions[0]
                  : stage < 3
                    ? animationDescriptions[stage - 1]
                    : animationDescriptions[2]}
              </Description>
            </AnimationText>
            <AnimationSVG>
              <ProductAnimation stage={stage} />
            </AnimationSVG>
          </AnimationContainer>
        </ProductSlider>
        <Doots id="dootsInSlider" stage={stage}>
          <DottedLine /> {" "}
          {[1, 2, 3].map(index =>
            <DootWrapper
              active={index === dootStage}
              stage={dootStage}
              index={index}
            >
              <Doot
                stage={dootStage}
                index={index}
                onClick={() =>
                  document
                    .getElementById("stage-" + index)
                    .scrollIntoView(true)}
              />
            </DootWrapper>
          )}
        </Doots>
      </Grid>
    </ProductTracks>
  );
};

const ProductAnimationSlider = ({
  animationTitles = [""],
  animationDescriptions = [""],
  stage = 0,
  onStageChange,
  titleForFrame,
  ...props
}) => {
  return (
    <div>
      <Animation
        stage={stage}
        onStageChange={onStageChange}
        animationTitles={animationTitles}
        animationDescriptions={animationDescriptions}
      />
    </div>
  );
};

export default withState("stage", "onStageChange", 0)(ProductAnimationSlider);



// WEBPACK FOOTER //
// ./src/components/ProductAnimationSlider.js